import React, { useContext } from 'react'
import YotpoStars from 'src/components/YotpoStars'
import theme from 'src/utils/styles/theme'
import { graphql, useStaticQuery } from 'gatsby'
import { GatsbyImage } from 'gatsby-plugin-image'
import Analytics from 'src/utils/Analytics'
import { HiddenOnDesktop, HiddenOnMobile } from 'src/utils/styles/global-styles'
import PropTypes from 'prop-types'
import { BuyNowLink, BuyNowAnchor, ExternalLink } from '../Hero/styles'
import StoreContext from '../../context/StoreContext'
import { TextContainer } from '../HomepageHero/styles'

import {
  Heading,
  Container,
  ImageContainer,
  CtaContainer,
  LogoImageContainer,
  SubHeading,
  SyncsHeader,
  SyncsSection,
  Flexbox,
} from './styles'
import {
  BUY_LINK,
  CAL_BUY_LINK,
  FRAME_2_WALNUT_BUY_LINK,
  IS_FOREIGN_COUNTRY,
  IN_CA,
} from '../../utils/constants'

export default function HomepageCalHero({ page, amazon }) {
  const data = useStaticQuery(graphql`
    query HomepageCalHeroQuery {
      calBackground: file(relativePath: { eq: "homepage/calendar-on-counter-desktop.png" }) {
        childImageSharp {
          gatsbyImageData(quality: 100, layout: FULL_WIDTH, placeholder: NONE)
        }
      }
      calBackgroundMD: file(relativePath: { eq: "homepage/calendar-on-counter-desktop-md.webp" }) {
        childImageSharp {
          gatsbyImageData(quality: 100, layout: FULL_WIDTH, placeholder: NONE)
        }
      }
      calBackgroundCalendarMD: file(
        relativePath: { eq: "homepage/calendar-on-counter-desktop-calendar-md.webp" }
      ) {
        childImageSharp {
          gatsbyImageData(quality: 100, layout: FULL_WIDTH, placeholder: NONE)
        }
      }
      bts: file(relativePath: { eq: "bts/calendar-15-back-to-school.webp" }) {
        childImageSharp {
          gatsbyImageData(quality: 100, layout: FULL_WIDTH, placeholder: NONE)
        }
      }
      calBackgroundMobile: file(relativePath: { eq: "homepage/calendar-on-counter-mobile.png" }) {
        childImageSharp {
          gatsbyImageData(quality: 100, layout: FULL_WIDTH, placeholder: NONE)
        }
      }
      calBackgroundMobileMD: file(
        relativePath: { eq: "homepage/calendar-on-counter-mobile-md.webp" }
      ) {
        childImageSharp {
          gatsbyImageData(quality: 100, layout: FULL_WIDTH, placeholder: NONE)
        }
      }
      calBackgroundCalendarMobileMD: file(
        relativePath: { eq: "homepage/calendar-on-counter-calendar-mobile-md.webp" }
      ) {
        childImageSharp {
          gatsbyImageData(quality: 100, layout: FULL_WIDTH, placeholder: NONE)
        }
      }
      google: file(relativePath: { eq: "icons/google.webp" }) {
        childImageSharp {
          gatsbyImageData(quality: 100, layout: FULL_WIDTH, placeholder: NONE)
        }
      }
      outlook: file(relativePath: { eq: "icons/outlook.webp" }) {
        childImageSharp {
          gatsbyImageData(quality: 100, layout: FULL_WIDTH, placeholder: NONE)
        }
      }
      apple: file(relativePath: { eq: "icons/apple.webp" }) {
        childImageSharp {
          gatsbyImageData(quality: 100, layout: FULL_WIDTH, placeholder: NONE)
        }
      }
      cozi: file(relativePath: { eq: "icons/cozi.webp" }) {
        childImageSharp {
          gatsbyImageData(quality: 100, layout: FULL_WIDTH, placeholder: NONE)
        }
      }
      yahoo: file(relativePath: { eq: "icons/yahoo.webp" }) {
        childImageSharp {
          gatsbyImageData(quality: 100, layout: FULL_WIDTH, placeholder: NONE)
        }
      }
      link: file(relativePath: { eq: "external-link.webp" }) {
        childImageSharp {
          gatsbyImageData(quality: 100, placeholder: NONE)
        }
      }
    }
  `)

  const calendarList = [
    { id: 'google', label: 'Google' },
    { id: 'outlook', label: 'Outlook' },
    { id: 'apple', label: 'Apple' },
    { id: 'cozi', label: 'Cozi' },
    { id: 'yahoo', label: 'Yahoo' },
  ]
  const {
    store: {
      banner: { promoCode },
    },
  } = useContext(StoreContext)
  const HEADING_OVERRIDE = {
    calendar: 'Drop the Stress. Not the Ball.',
  }
  const SUBHEADING_OVERRIDE = {
    calendar:
      'Get the whole family organized with chore charts, dinner planning, and customizable lists — all on one display the whole family can use.',
  }

  const DESKTOP_IMAGE_OVERRIDE = {
    calendar: data.calBackgroundCalendarMD.childImageSharp.gatsbyImageData,
  }
  let image =
    DESKTOP_IMAGE_OVERRIDE[page] ||
    DESKTOP_IMAGE_OVERRIDE[promoCode] ||
    (!IS_FOREIGN_COUNTRY
      ? data.bts.childImageSharp.gatsbyImageData
      : data.calBackground.childImageSharp.gatsbyImageData)

  const MOBILE_IMAGE_OVERRIDE = {
    calendar: data.calBackgroundCalendarMD.childImageSharp.gatsbyImageData,
    VIPBTS: data.bts.childImageSharp.gatsbyImageData,
  }
  let mobileImage =
    MOBILE_IMAGE_OVERRIDE[page] ||
    MOBILE_IMAGE_OVERRIDE[promoCode] ||
    (!IS_FOREIGN_COUNTRY
      ? data.bts.childImageSharp.gatsbyImageData
      : data.calBackgroundMobile.childImageSharp.gatsbyImageData)

  if (!IS_FOREIGN_COUNTRY && page === 'calendar') {
    image = data.calBackground.childImageSharp.gatsbyImageData
    mobileImage = data.calBackgroundMobile.childImageSharp.gatsbyImageData
  }
  const CTA_OVERRIDE = { calendar: 'Buy Now' }
  const getAmazonLink = () => {
    if (IN_CA) return 'https://urlgeni.us/amazon/DH2zq'
    return 'https://urlgeni.us/amazon/ShopifyCal15Amazon'
  }

  const AMAZON_LINK = getAmazonLink()
  return (
    <Container>
      <HiddenOnDesktop>
        <ImageContainer>
          <GatsbyImage
            image={mobileImage}
            alt='Skylight Calendar 15" on a countertop with a plant and bookcase in the background'
          />
        </ImageContainer>
      </HiddenOnDesktop>
      <TextContainer singleSlide>
        <YotpoStars color={theme.orange} textcolor="#444444" productId="6863425175604" />
        <Heading small={page === 'calendar'}>
          {HEADING_OVERRIDE[page] ||
            HEADING_OVERRIDE[promoCode] ||
            (!IS_FOREIGN_COUNTRY
              ? 'Skylight Runs the School Year'
              : 'The All-In-One Smart Family Calendar')}
        </Heading>
        <SubHeading>
          {SUBHEADING_OVERRIDE[page] ||
            SUBHEADING_OVERRIDE[promoCode] ||
            (!IS_FOREIGN_COUNTRY
              ? 'Supply lists, class schedules, lunch menus, extracurriculars, & more. Our best-selling digital calendar keeps track of it all.'
              : 'Get the whole family organized with scheduling, meal planning, chore charts and customizable lists — all on one beautiful display.')}
        </SubHeading>
        <Flexbox>
          <CtaContainer gap>
            {amazon && (
              <BuyNowAnchor
                href={AMAZON_LINK}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => Analytics.track('Clicked to Amazon Calendar')}
              >
                Buy Now on Amazon
                <ExternalLink
                  image={data.link.childImageSharp.gatsbyImageData}
                  alt="external link"
                />
              </BuyNowAnchor>
            )}
            {page !== 'frame2' && (
              <BuyNowLink
                onClick={() => Analytics.track('Clicked Buy Now Calendar Hero on Homepage CTA')}
                to={CAL_BUY_LINK}
              >
                {CTA_OVERRIDE[page] || 'Shop Calendar'}
              </BuyNowLink>
            )}
            {IS_FOREIGN_COUNTRY && page !== 'calendar' && promoCode !== 'VIPBTS' && (
              <BuyNowLink
                onClick={() => Analytics.track('Clicked Buy Now Frame Hero on Homepage CTA')}
                to={page === 'frame2' ? FRAME_2_WALNUT_BUY_LINK : BUY_LINK}
              >
                {CTA_OVERRIDE[page] || 'Shop Frame'}
              </BuyNowLink>
            )}
          </CtaContainer>
          {page !== 'frame2' && (
            <SyncsSection>
              <SyncsHeader>Seamlessly auto-syncs with:</SyncsHeader>
              <CtaContainer>
                {calendarList.map(({ id, label }) => (
                  <LogoImageContainer>
                    <GatsbyImage
                      key={id}
                      image={data[id].childImageSharp.gatsbyImageData}
                      alt={`Skylight Calendar is compatible with ${label}`}
                    />
                  </LogoImageContainer>
                ))}
              </CtaContainer>
            </SyncsSection>
          )}
        </Flexbox>
      </TextContainer>
      <HiddenOnMobile>
        <ImageContainer>
          <GatsbyImage
            image={image}
            alt='Skylight Calendar 15" on a countertop with a plant and bookcase in the background'
          />
        </ImageContainer>
      </HiddenOnMobile>
    </Container>
  )
}

HomepageCalHero.propTypes = {
  page: PropTypes.string.isRequired,
  amazon: PropTypes.bool,
}
